// DemoRequestModal.js
import React, { useState } from 'react';
import { X, Mail, CheckCircle, AlertCircle } from 'lucide-react';
import Button from '../../Button/Button';
import { api } from "../../../api";
import './DemoRequestModal.css';

const DemoRequestModal = ({ isOpen, onClose, onShowAlert }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null, 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateEmail = (email) => {
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset previous states
    setErrorMessage('');
    setSubmitStatus(null);

    // Validate email
    if (!email.trim()) {
      setErrorMessage('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setErrorMessage('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      // API call to submit demo request
      const response = await api.post('/demo/request', {
        email: email.trim()
      });

      // Check if the response indicates success
      if (response.data && response.data.success) {
        if (response.data.isDuplicate) {
          // Handle duplicate email gracefully
          handleClose();
          // Show alert notification
          if (onShowAlert) {
            onShowAlert({
              type: 'success',
              title: 'Thank you!',
              message: 'We have your details and will be in contact within 2-3 business days.',
              duration: 5000
            });
          }
        } else {
          // New submission
          setSubmitStatus('success');
          setEmail(''); // Clear the form
        }
      } else {
        throw new Error(response.data?.message || 'Failed to submit demo request');
      }

    } catch (error) {
      console.error('Error submitting demo request:', error);
      setSubmitStatus('error');
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const errorData = error.response.data;
        if (errorData && errorData.message) {
          setErrorMessage(errorData.message);
        } else if (errorData && errorData.errors && errorData.errors.length > 0) {
          setErrorMessage(errorData.errors[0].msg || 'Validation error');
        } else {
          setErrorMessage('Server error. Please try again.');
        }
      } else if (error.request) {
        // Network error
        setErrorMessage('Network error. Please check your connection and try again.');
      } else {
        // Other error
        setErrorMessage(error.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset all states when closing
    setEmail('');
    setIsSubmitting(false);
    setSubmitStatus(null);
    setErrorMessage('');
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="demo-modal-overlay"
      onClick={handleOverlayClick}
    >
      <div className="demo-modal-content">
        {/* Header */}
        <div className="demo-modal-header">
          <div>
            <h2 className="demo-modal-title">Request a Demo</h2>
            <p className="demo-modal-subtitle">
              See how fdbk can transform your customer feedback process
            </p>
          </div>
          <button
            onClick={handleClose}
            className="demo-modal-close-btn"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="demo-modal-body">
          {submitStatus === 'success' ? (
            // Success State
            <div className="demo-success-content">
              <div className="demo-success-icon">
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
              <h3 className="demo-success-title">Demo Request Submitted!</h3>
              <p className="demo-success-message">
                Thank you for your interest in fdbk. Our team will contact you within 2 business days.
              </p>
              <Button
                variant="primary"
                size="large"
                onClick={handleClose}
                className="demo-success-btn"
              >
                Got it!
              </Button>
            </div>
          ) : (
            // Form State
            <form onSubmit={handleSubmit} className="demo-form">
              <div className="demo-form-group">
                <label htmlFor="email" className="demo-form-label">
                  <Mail className="w-4 h-4 mr-2" />
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your business email"
                  className={`demo-form-input ${errorMessage ? 'error' : ''}`}
                  disabled={isSubmitting}
                />
                {errorMessage && (
                  <div className="demo-form-error">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errorMessage}
                  </div>
                )}
              </div>

              <div className="demo-form-info">
                <p className="demo-info-text">
                  We'll use this email to:
                </p>
                <ul className="demo-info-list">
                  <li>Introduce our product and ourselves to you</li>
                  <li>Send you demo materials and next steps</li>
                  <li>Answer any questions about fdbk</li>
                </ul>
              </div>

              {submitStatus === 'error' && (
                <div className="demo-form-error-banner">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  <span>Failed to submit request. Please try again.</span>
                </div>
              )}

              <div className="demo-form-actions">
                <Button
                  type="button"
                  variant="outline"
                  size="large"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="demo-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="large"
                  loading={isSubmitting}
                  className="demo-submit-btn"
                >
                  {isSubmitting ? 'Submitting...' : 'Request Demo'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoRequestModal;